import { friendlyDbError } from '../utils/friendlyDbError';
import { mergeProductPrices } from './productPricesMerge';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { autoSyncService } from '../services/autoSyncService';
import { woocommerceService, WooCommerceUser } from '../services/woocommerceService';

export interface PriceList {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  is_default: boolean;
  discount_percentage: number;
  currency: string;
  is_active: boolean;
  is_rental: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductPriceListPrice {
  id: string;
  organization_id: string;
  product_id: string;
  price_list_id: string;
  regular_price: number;
  sale_price?: number;
  sale_price_from?: string;
  sale_price_to?: string;
  min_quantity: number;
  max_quantity?: number;
  markup_percentage?: number | null;
  created_at: string;
  updated_at: string;

  // Relations
  price_list?: PriceList;
}

// Cache per utenti WooCommerce
interface WooCommerceUserCache {
  data: WooCommerceUser[];
  timestamp: number;
  priceListId: string;
}

// TTL cache: 5 minuti
const WOO_CACHE_TTL = 5 * 60 * 1000;

interface PriceListStore {
  priceLists: PriceList[];
  productPrices: ProductPriceListPrice[];
  loading: boolean;
  error: string | null;

  // WooCommerce users state
  wooUserCache: Map<string, WooCommerceUserCache>;
  wooUsersLoading: boolean;
  wooUsersError: string | null;
  wooConfigured: boolean | null;

  // Actions
  fetchPriceLists: () => Promise<void>;
  createPriceList: (priceList: Omit<PriceList, 'id' | 'created_at' | 'updated_at'>) => Promise<PriceList | null>;
  updatePriceList: (id: string, updates: Partial<PriceList>) => Promise<void>;
  deletePriceList: (id: string) => Promise<void>;
  setDefaultPriceList: (id: string) => Promise<void>;

  // Product prices
  fetchProductPrices: (productId?: string) => Promise<void>;
  clearProductPrices: () => void;
  upsertProductPrice: (price: Omit<ProductPriceListPrice, 'created_at' | 'updated_at'> & { id?: string }) => Promise<void>;
  deleteProductPrice: (id: string) => Promise<void>;

  // Utility
  getDefaultPriceList: () => PriceList | null;
  getPriceListById: (id: string) => PriceList | null;
  getProductPricesForList: (productId: string, priceListId: string) => ProductPriceListPrice[];
  getBestPriceForQuantity: (productId: string, priceListId: string, quantity: number) => ProductPriceListPrice | null;

  // Client associations
  fetchClientsByPriceList: (priceListId: string) => Promise<any[]>;

  // Rental specific
  fetchRentalPriceLists: () => Promise<void>;
  getRentalPriceLists: () => PriceList[];

  // WooCommerce users
  checkWooCommerceConfigured: () => Promise<boolean>;
  fetchWooCommerceUsersByPriceList: (priceListId: string, forceRefresh?: boolean) => Promise<WooCommerceUser[]>;
  clearWooUserCache: () => void;
}

export const usePriceListStore = create<PriceListStore>((set, get) => ({
  priceLists: [],
  productPrices: [],
  loading: false,
  error: null,

  // WooCommerce users state
  wooUserCache: new Map(),
  wooUsersLoading: false,
  wooUsersError: null,
  wooConfigured: null,

  fetchPriceLists: async () => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('price_lists')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      set({ priceLists: data || [], loading: false });
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  createPriceList: async (priceListData) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      // Se questo listino è impostato come default, rimuovi il flag da tutti gli altri
      if (priceListData.is_default) {
        await supabase
          .from('price_lists')
          .update({ is_default: false })
          .eq('organization_id', profile.organization_id);
      }

      const { data, error } = await supabase
        .from('price_lists')
        .insert({
          ...priceListData,
          organization_id: profile.organization_id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        priceLists: [...state.priceLists, data],
        loading: false
      }));

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
      return null;
    }
  },

  updatePriceList: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      // Se questo listino è impostato come default, rimuovi il flag da tutti gli altri
      if (updates.is_default) {
        await supabase
          .from('price_lists')
          .update({ is_default: false })
          .eq('organization_id', profile.organization_id)
          .neq('id', id);
      }

      const { error } = await supabase
        .from('price_lists')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        priceLists: state.priceLists.map(pl => 
          pl.id === id ? { ...pl, ...updates } : pl
        ),
        loading: false
      }));
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  deletePriceList: async (id) => {
    set({ loading: true, error: null });
    try {
      const priceList = get().getPriceListById(id);
      if (priceList?.is_default) {
        throw new Error('Non è possibile eliminare il listino di default');
      }

      const { error } = await supabase
        .from('price_lists')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        priceLists: state.priceLists.filter(pl => pl.id !== id),
        productPrices: state.productPrices.filter(pp => pp.price_list_id !== id),
        loading: false
      }));
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  setDefaultPriceList: async (id) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      // Rimuovi il flag default da tutti
      await supabase
        .from('price_lists')
        .update({ is_default: false })
        .eq('organization_id', profile.organization_id);

      // Imposta il nuovo default
      await supabase
        .from('price_lists')
        .update({ is_default: true })
        .eq('id', id);

      // Aggiorna lo stato locale
      set(state => ({
        priceLists: state.priceLists.map(pl => ({
          ...pl,
          is_default: pl.id === id
        }))
      }));
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  fetchProductPrices: async (productId) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      // A pagine da 1000: il server restituisce al massimo 1000 righe per
      // richiesta e con più fasce per prodotto il limite si supera presto
      // (es. 4 fasce x 250 prodotti), lasciando prodotti senza prezzi listino.
      const PAGE_SIZE = 1000;
      const data: ProductPriceListPrice[] = [];
      for (let from = 0; ; from += PAGE_SIZE) {
        let query = supabase
          .from('product_price_list_prices')
          .select(`
            *,
            price_list:price_lists(*)
          `)
          .eq('organization_id', profile.organization_id);

        if (productId) {
          query = query.eq('product_id', productId);
        }

        const { data: page, error } = await query
          .order('min_quantity')
          .order('id')
          .range(from, from + PAGE_SIZE - 1);

        if (error) throw error;
        data.push(...((page || []) as ProductPriceListPrice[]));
        if (!page || page.length < PAGE_SIZE) break;
      }
      // Con un productId si aggiornano solo le righe di quel prodotto:
      // la lista prodotti del magazzino usa i prezzi di tutti i prodotti.
      set(state => ({
        productPrices: mergeProductPrices(state.productPrices, data || [], productId),
        loading: false,
      }));
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  clearProductPrices: () => {
    set({ productPrices: [] });
  },

  upsertProductPrice: async (priceData) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      console.log('🔧 Upserting price with data:', {
        hasId: !!priceData.id,
        id: priceData.id,
        product_id: priceData.product_id,
        price_list_id: priceData.price_list_id,
        min_quantity: priceData.min_quantity,
        max_quantity: priceData.max_quantity
      });

      // Check if a price already exists with this combination
      const { data: existingPrice } = await supabase
        .from('product_price_list_prices')
        .select('id')
        .eq('product_id', priceData.product_id)
        .eq('price_list_id', priceData.price_list_id)
        .eq('min_quantity', priceData.min_quantity || 1)
        .eq('organization_id', profile.organization_id)
        .maybeSingle();

      let data, error;

      if (existingPrice) {
        // Update existing record
        console.log('📝 Updating existing price:', existingPrice.id);
        const result = await supabase
          .from('product_price_list_prices')
          .update({
            regular_price: priceData.regular_price,
            sale_price: priceData.sale_price,
            sale_price_from: priceData.sale_price_from,
            sale_price_to: priceData.sale_price_to,
            min_quantity: priceData.min_quantity,
            max_quantity: priceData.max_quantity,
            markup_percentage: priceData.markup_percentage
          })
          .eq('id', existingPrice.id)
          .select()
          .single();

        data = result.data;
        error = result.error;
      } else {
        // Insert new record
        console.log('➕ Inserting new price');
        const result = await supabase
          .from('product_price_list_prices')
          .insert({
            ...priceData,
            organization_id: profile.organization_id,
            min_quantity: priceData.min_quantity || 1
          })
          .select()
          .single();

        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error('❌ Upsert error:', error);
        throw error;
      }

      console.log('✅ Upsert successful:', data);

      // Fetch the price_list relation separately
      if (data) {
        const { data: priceListData } = await supabase
          .from('price_lists')
          .select('*')
          .eq('id', data.price_list_id)
          .single();

        if (priceListData) {
          data.price_list = priceListData;
        }
      }

      set(state => {
        const existingIndex = state.productPrices.findIndex(
          pp => pp.product_id === priceData.product_id &&
                pp.price_list_id === priceData.price_list_id &&
                pp.min_quantity === priceData.min_quantity
        );

        if (existingIndex >= 0) {
          const updated = [...state.productPrices];
          updated[existingIndex] = data;
          return { productPrices: updated, loading: false };
        } else {
          return {
            productPrices: [...state.productPrices, data],
            loading: false
          };
        }
      });

      // NOTE: Non chiamare triggerSync qui - verrà chiamato una sola volta
      // dal chiamante (es. handleSave in ProductPriceListSection) dopo che
      // tutti i prezzi sono stati salvati, per evitare toast multipli
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  deleteProductPrice: async (id) => {
    set({ loading: true, error: null });
    try {
      // Recuperare product_id PRIMA della delete per auto-sync
      const { data: priceRecord } = await supabase
        .from('product_price_list_prices')
        .select('product_id')
        .eq('id', id)
        .single();

      const { error } = await supabase
        .from('product_price_list_prices')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        productPrices: state.productPrices.filter(pp => pp.id !== id),
        loading: false
      }));

      // Trigger auto-sync per aggiornare i prezzi del prodotto su WooCommerce
      if (priceRecord?.product_id) {
        autoSyncService.triggerSync('price', priceRecord.product_id, 'update');
      }
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  // Utility functions
  getDefaultPriceList: () => {
    return get().priceLists.find(pl => pl.is_default) || null;
  },

  getPriceListById: (id) => {
    return get().priceLists.find(pl => pl.id === id) || null;
  },

  getProductPricesForList: (productId, priceListId) => {
    return get().productPrices.filter(
      pp => pp.product_id === productId && pp.price_list_id === priceListId
    );
  },

  getBestPriceForQuantity: (productId, priceListId, quantity) => {
    const prices = get().getProductPricesForList(productId, priceListId);
    
    // Trova il prezzo migliore per la quantità richiesta
    const validPrices = prices.filter(price => 
      quantity >= price.min_quantity && 
      (price.max_quantity === null || quantity <= price.max_quantity)
    );

    // Ordina per quantità minima decrescente (prende quello con quantità più alta applicabile)
    validPrices.sort((a, b) => b.min_quantity - a.min_quantity);
    
    return validPrices[0] || null;
  },

  // Client associations
  fetchClientsByPriceList: async (priceListId) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('clients')
        .select('id, business_name, entity_type, email, phone, stato_cliente')
        .eq('organization_id', profile.organization_id)
        .eq('default_price_list_id', priceListId)
        .order('business_name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching clients by price list:', error);
      return [];
    }
  },

  // Rental specific functions
  fetchRentalPriceLists: async () => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('price_lists')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .eq('is_rental', true)
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      
      // Update only rental price lists in the store, keeping others
      const currentPriceLists = get().priceLists;
      const nonRentalLists = currentPriceLists.filter(pl => !pl.is_rental);
      set({ priceLists: [...nonRentalLists, ...(data || [])], loading: false });
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  getRentalPriceLists: () => {
    return get().priceLists.filter(pl => pl.is_rental && pl.is_active);
  },

  // WooCommerce users methods
  checkWooCommerceConfigured: async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) return false;

      const { data: config } = await supabase
        .from('api_configurations')
        .select('id, is_active')
        .eq('organization_id', profile.organization_id)
        .eq('service_name', 'woocommerce')
        .eq('is_active', true)
        .maybeSingle();

      const isConfigured = !!config;
      set({ wooConfigured: isConfigured });
      return isConfigured;
    } catch (error) {
      console.error('Error checking WooCommerce config:', error);
      set({ wooConfigured: false });
      return false;
    }
  },

  fetchWooCommerceUsersByPriceList: async (priceListId: string, forceRefresh = false) => {
    const { wooUserCache, wooConfigured } = get();

    // Se WooCommerce non è configurato, ritorna array vuoto
    if (wooConfigured === false) {
      return [];
    }

    // Check cache
    const cached = wooUserCache.get(priceListId);
    if (!forceRefresh && cached && (Date.now() - cached.timestamp) < WOO_CACHE_TTL) {
      return cached.data;
    }

    set({ wooUsersLoading: true, wooUsersError: null });

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      // Inizializza il service e chiama l'endpoint
      await woocommerceService.initialize(profile.organization_id);
      const result = await woocommerceService.getUsersByPriceList(priceListId);

      if (!result.success) {
        // Se errore di connessione, potrebbe essere WooCommerce non configurato
        if (result.error?.includes('not found') || result.error?.includes('404') || result.error?.includes('not initialized')) {
          set({ wooConfigured: false, wooUsersLoading: false });
          return [];
        }
        throw new Error(result.error);
      }

      // Aggiorna cache
      const newCache = new Map(wooUserCache);
      newCache.set(priceListId, {
        data: result.data,
        timestamp: Date.now(),
        priceListId
      });

      set({
        wooUserCache: newCache,
        wooUsersLoading: false,
        wooConfigured: true
      });

      return result.data;
    } catch (error) {
      const errorMsg = friendlyDbError(error);
      // Non mostrare errore se WooCommerce semplicemente non è configurato
      if (errorMsg.includes('configuration not found') || errorMsg.includes('not initialized')) {
        set({ wooConfigured: false, wooUsersLoading: false });
        return [];
      }
      set({
        wooUsersError: errorMsg,
        wooUsersLoading: false
      });
      return [];
    }
  },

  clearWooUserCache: () => {
    set({ wooUserCache: new Map() });
  }
}));